/* eslint-disable max-len */
/* eslint-disable max-lines */
/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './changeRequest.scss';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import ChangeRequestDialog from './Dialogs/ChangeRequestDialog';
import CRApprovalHistory from './CRApprovalHistory';
import CRDetailsDialog from './Dialogs/CRDetailsDialog';
import { useAuth } from '../../../Hooks/useAuth';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { CHANGE_REQUEST } from '../../../Api/resources/endPoint';
import { getExportFileName, getExportFileNameWithDate } from '../../Delivery/utils';
import CRStatusUpdateDialog from './Dialogs/CRStatusUpdateDialog';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { fetchCRDetails, approveCRRequest } from '../../../Api/request/projectChangeReq';
import { MODULE_NAMES } from '../../../Constants/modules';

const PAGE_SIZE = 10;
const INITIAL_PAGINATION = { page: 1, limit: PAGE_SIZE, total: 0 };

const buildServiceURL = (service, path) => {
  const url = appendServiceNameToURL(service);
  if (process.env.NODE_ENV === 'production') {
    return `${process.env.REACT_APP_API_URL}${url}${path}`;
  }
  const port = appendServicePortToURL(service);
  const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, '');
  return `${baseUrl}:${port}${url}${path}`;
};

const baseURL = buildServiceURL(serviceName.PROJECT_MS, `/api${CHANGE_REQUEST}`);
const crApprovalHistoryBaseURL = buildServiceURL(serviceName.PROJECT_MS, `/api${CHANGE_REQUEST}/cr-approval-history`);

const DEFAULT_CR_APPROVAL_FILTERS = { additionalFilters: { requestStatus: ['OPEN'] } };

const buildInitialFilters = (rawProjectId, defaultStatus = ['0'], rawProjectName = null) => {
  const filters = [];

  // Always inject approvalStatus: '0' by default if defaultStatus is provided
  if (defaultStatus) {
    const statusValues = Array.isArray(defaultStatus)
      ? defaultStatus.map(String)
      : [String(defaultStatus)];

    filters.push({
      field: 'approvalStatus',
      selectedValues: statusValues,
    });
  }

  // Inject projectName filter only if rawProjectId exists
  if (rawProjectId) {
    const rawProjectIdStr = String(rawProjectId);
    const labelVal = rawProjectName || rawProjectIdStr;

    const filterItem = {
      field: 'projectName',
      label: labelVal,
      selectedLabel: labelVal,
      selectedValue: { label: labelVal, value: rawProjectIdStr },
      selectedValues: [rawProjectIdStr],
    };

    filters.push(filterItem);
  }

  return filters;
};

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;
  rootNode.querySelectorAll('.button-group button').forEach((btn) => {
    const isReset = Boolean(btn.querySelector('.pi-refresh'));
    const isFilter = btn.classList.contains('table-utility-filter-btn');
    const isNew = btn.classList.contains('table-utility-add-new-btn');
    const isExport = !isFilter && !isReset && !isNew && Boolean(btn.querySelector('svg'));
    btn.classList.toggle('change-request-reset-btn', isReset);
    btn.classList.toggle('change-request-export-btn', isExport);
    btn.classList.toggle('change-request-add-btn', isNew);
  });
};

const applyTotalRowClass = (container) => {
  if (!container) return;
  const tbody = container.querySelector('.p-datatable-tbody');
  if (!tbody) return;
  const rows = [...tbody.querySelectorAll('tr')];
  let totalRow = null;

  rows.forEach((row) => {
    const hasTotalText = [...row.cells].some((cell) => cell.innerText?.trim() === 'Total');
    if (hasTotalText) {
      row.classList.add('change-request-total-row');
      totalRow = row;
    }
  });
  if (totalRow && tbody.lastElementChild !== totalRow) {
    tbody.appendChild(totalRow);
  }
};

const ChangeRequest = () => {
  const { userProfile } = useAuth();
  const tableContainerRef = useRef(null);
  const tableStateRef = useRef(null);
  const crApprovalTableRef = useRef(null);
  const crApprovalTableStateRef = useRef(null);
  const moreMenuRef = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const mainPaginationRef = useRef(INITIAL_PAGINATION);
  const crApprovalPaginationRef = useRef(INITIAL_PAGINATION);
  const [, forceUpdate] = useState(0);
  const triggerPaginationRender = useCallback(() => forceUpdate((n) => n + 1), []);

  const [mainExternalFilters, setMainExternalFilters] = useState(
    () => buildInitialFilters(searchParams.get('projectId'), null, searchParams.get('projectName')),
  );
  const capturedMainFiltersRef = useRef(mainExternalFilters);

  const [crApprovalContext] = useState({
    defaultFilters: DEFAULT_CR_APPROVAL_FILTERS,
  });

  // Inject approvalStatus: ['0'] by default regardless of projectId presence
  const [crApprovalExternalFilters, setCrApprovalExternalFilters] = useState(
    () => buildInitialFilters(searchParams.get('projectId'), ['0'], searchParams.get('projectName')),
  );
  const capturedCrApprovalFiltersRef = useRef(crApprovalExternalFilters);

  const [crDetailsDialogVisible, setCrDetailsDialogVisible] = useState(false);
  const [crDetailsData, setCrDetailsData] = useState(null);
  const [crStatusUpdateDialogVisible, setCrStatusUpdateDialogVisible] = useState(false);
  const [, setCrStatusFormData] = useState({ status: null, remarks: '' });
  const [newDialogVisible, setNewDialogVisible] = useState(false);
  const [didUserApplyFilters, setDidUserApplyFilters] = useState(false);

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isSingleProject, setIsSingleProject] = useState(false);

  // Opens approval history if URL contains ?view=approval-history
  const [showCRApprovalHistory] = useState(
    () => searchParams.get('view') === 'approval-history',
  );

  const [currentProject, setCurrentProject] = useState(null);
  const [isCrApprovalInitialLoad, setIsCrApprovalInitialLoad] = useState(true);
  const domEnhancementRafRef = useRef(null);

  const [infoMessage] = useState(
    'Showing pending requests by default. Use the Status filter to view others.',
  );

  const canCreateCR = useMemo(() => {
    const perms = userProfile?.permissions?.[MODULE_NAMES.PROJECT_CHANGE_REQUEST] || [];
    return perms.includes('create');
  }, [userProfile]);

  const canViewCRList = useMemo(() => {
    const perms = userProfile?.permissions?.[MODULE_NAMES.PROJECT_CHANGE_REQUEST] || [];
    return perms.includes('view') || perms.includes('read');
  }, [userProfile]);

  const canEditCR = useMemo(() => {
    const perms = userProfile?.permissions?.[MODULE_NAMES.PROJECT_CHANGE_REQUEST] || [];
    return perms.includes('update') || perms.includes('edit');
  }, [userProfile]);

  const canApproveRejectCR = useMemo(() => {
    const perms = userProfile?.permissions?.[MODULE_NAMES.PROJECT_CHANGE_REQUEST_APPROVE_REJECT_REQUEST] || [];
    return perms.includes('update') || perms.includes('approve') || perms.includes('reject');
  }, [userProfile]);

  const canViewCRDetails = useMemo(() => {
    const perms = userProfile?.permissions?.[MODULE_NAMES.PROJECT_CHANGE_REQUEST] || [];
    return perms.includes('view') || perms.includes('read');
  }, [userProfile]);

  const canViewCRApprovalHistory = useMemo(() => {
    const perms = userProfile?.permissions?.[MODULE_NAMES.PROJECT_CHANGE_REQUEST] || [];
    return perms.includes('view') || perms.includes('read');
  }, [userProfile]);

  const isSuperAdmin = useMemo(() => {
    return userProfile?.roleDetails?.some(
      (role) => role.roleName === 'Super Admin',
    );
  }, [userProfile]);

  const canCreateCRFinal = isSuperAdmin || canCreateCR;

  const normalizeCapturedFilters = useCallback((filters) => {
    if (!Array.isArray(filters)) return [];

    return filters
      .filter((filter) => filter && typeof filter === 'object' && typeof filter.field === 'string')
      .map((filter) => {
        const minimalFilter = { field: filter.field };

        if (Array.isArray(filter.rangePlaceholders) && filter.rangePlaceholders.length > 0) {
          minimalFilter.rangePlaceholders = [...filter.rangePlaceholders];
        }

        if (Array.isArray(filter.range) && filter.range.some((v) => v !== null && v !== undefined && v !== '')) {
          minimalFilter.range = [...filter.range];
        }

        if (Array.isArray(filter.selectedValues) && filter.selectedValues.length > 0) {
          minimalFilter.selectedValues = [...filter.selectedValues];
        }

        if (filter.label !== null && filter.label !== undefined && filter.label !== '') {
          minimalFilter.label = filter.label;
        }

        if (filter.selectedLabel !== null && filter.selectedLabel !== undefined && filter.selectedLabel !== '') {
          minimalFilter.selectedLabel = filter.selectedLabel;
        }

        if (filter.selectedValue !== null && filter.selectedValue !== undefined && filter.selectedValue !== '') {
          minimalFilter.selectedValue = filter.selectedValue;
        }

        if (filter.condition !== null && filter.condition !== undefined && filter.condition !== '') {
          minimalFilter.condition = filter.condition;
        }

        if (filter.value !== null && filter.value !== undefined && filter.value !== '') {
          minimalFilter.value = filter.value;
        }

        return minimalFilter;
      });
  }, []);

  const fetchCrViewData = useCallback(async (rowData) => {
    try {
      let crId = rowData?.cr_id || rowData?.crId || rowData?.id || rowData?.cr_id_val;
      if (!crId && rowData?.crNumber) {
        const m = String(rowData.crNumber).match(/(\d+)/);
        if (m) crId = Number(m[1]);
      }
      if (!crId && rowData?.backup?.back_up_id) crId = rowData.backup.back_up_id;
      if (!crId && rowData?.current?.cr_id) crId = rowData.current.cr_id;
      if (!crId) return;

      const token = userProfile?.access_token || '';
      const response = await fetchCRDetails({ authToken: token, crId });

      if (response?.status < 400 && response?.data) {
        const payload = response.data.data || response.data;
        setCrDetailsData(payload);
      }
    } catch {
      // silence error
    }
  }, [userProfile]);

  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;

    const payload = response.data?.data || response.data || {};
    const tableData = payload.tableData || [];

    // 1. Check if projectName filter is explicitly selected
    const projectFilter = capturedMainFiltersRef.current.find((f) => f.field === 'projectName');

    const isObjectSelection = projectFilter?.selectedValue !== null
    && typeof projectFilter?.selectedValue === 'object';

    const rawSelectedId = isObjectSelection
      ? projectFilter?.selectedValue?.value
      : (projectFilter?.selectedValue ?? projectFilter?.selectedValues?.[0]);

    // Ensure single project state ONLY if user explicitly selected ONE project in the filter
    const hasSingleProjectFilter = Boolean(
      rawSelectedId !== null
    && rawSelectedId !== undefined
    && rawSelectedId !== ''
    && (!Array.isArray(projectFilter?.selectedValues) || projectFilter?.selectedValues?.length === 1),
    );

    let nextIsSingle = false;
    let nextProject = null;

    if (hasSingleProjectFilter) {
      const selectedId = String(rawSelectedId);

      // Search tableData only to resolve the display name if needed
      const rowMatch = Array.isArray(tableData)
        ? tableData.find((row) => {
          const rowProjectId = row?.projectId ?? row?.projectDetail?.projectId;
          return rowProjectId !== undefined && rowProjectId !== null
            && String(rowProjectId) === selectedId;
        })
        : null;

      const resolvedName = (isObjectSelection && projectFilter.selectedValue?.label)
      || rowMatch?.projectDetail?.projectName
      || rowMatch?.projectName
      || null;

      nextIsSingle = true;
      nextProject = {
        projectId: selectedId,
        projectName: resolvedName,
      };
    }

    // 2. Guard state updates to prevent re-render cascades
    setIsSingleProject((prev) => (prev !== nextIsSingle ? nextIsSingle : prev));
    setCurrentProject((prev) => {
      if (!prev && !nextProject) return prev;
      if (
        prev?.projectId === nextProject?.projectId
      && prev?.projectName === nextProject?.projectName
      ) {
        return prev;
      }
      return nextProject;
    });

    // 3. Update pagination safely
    const pg = response.data?.pagination
             || response.data?.paginationConfig
             || response.data?.data?.pagination
             || response.data?.data?.paginationConfig;

    const newTotal = pg?.total ?? pg?.count ?? response.meta?.total
    ?? (Array.isArray(tableData) ? tableData.length : 0);
    const newLimit = pg?.limit ?? mainPaginationRef.current.limit;
    const newPage = pg?.page ?? response.meta?.page ?? mainPaginationRef.current.page;

    const next = {
      page: newPage,
      limit: newLimit,
      total: newTotal,
    };

    const prev = mainPaginationRef.current;
    if (prev.page !== next.page || prev.limit !== next.limit || prev.total !== next.total) {
      mainPaginationRef.current = next;
      triggerPaginationRender();
    }
  }, [triggerPaginationRender]);

  const handlePaginationChange = useCallback((newPagination) => {
    mainPaginationRef.current = {
      ...mainPaginationRef.current,
      ...newPagination,
    };
    triggerPaginationRender();
  }, [triggerPaginationRender]);

  const handleFilterUpdate = useCallback(({ action, filters }) => {
    if (action === 'reset') {
      capturedMainFiltersRef.current = [];
      setMainExternalFilters([]);
      setDidUserApplyFilters(false);
      mainPaginationRef.current = { ...INITIAL_PAGINATION };
      triggerPaginationRender();
      return;
    }

    if (action === 'apply') {
      const normalizedFilters = normalizeCapturedFilters(filters);
      setDidUserApplyFilters(true);
      capturedMainFiltersRef.current = normalizedFilters;
      setMainExternalFilters(normalizedFilters);
      mainPaginationRef.current = { ...mainPaginationRef.current, page: 1 };
      triggerPaginationRender();
    }
  }, [normalizeCapturedFilters, triggerPaginationRender]);

  const handleCrApprovalFilteredApiResponse = useCallback((response) => {
    if (!response) return;

    const payload = response.data?.data || response.data || {};
    const tableData = payload.tableData || [];

    const pg = response.data?.pagination
               || response.data?.paginationConfig
               || response.data?.data?.pagination
               || response.data?.data?.paginationConfig;

    const newTotal = pg?.total ?? pg?.count ?? response.meta?.total
    ?? (Array.isArray(tableData) ? tableData.length : 0);
    const newLimit = pg?.limit ?? crApprovalPaginationRef.current.limit;
    const newPage = pg?.page ?? response.meta?.page ?? crApprovalPaginationRef.current.page;

    const next = {
      page: newPage,
      limit: newLimit,
      total: newTotal,
    };
    setIsCrApprovalInitialLoad(false);

    const prev = crApprovalPaginationRef.current;
    if (prev.page !== next.page || prev.limit !== next.limit || prev.total !== next.total) {
      crApprovalPaginationRef.current = next;
      triggerPaginationRender();
    } else {
      crApprovalPaginationRef.current = next;
    }
  }, [triggerPaginationRender]);

  const handleCrApprovalPaginationChange = useCallback((newPagination) => {
    crApprovalPaginationRef.current = {
      ...crApprovalPaginationRef.current,
      ...newPagination,
    };
    triggerPaginationRender();
  }, [triggerPaginationRender]);

  // Updated handler: restores default approvalStatus filter on reset & syncs searchParams URL
  const handleCrApprovalFilterUpdate = useCallback(({ action, filters }) => {
    if (action === 'reset') {
      // Re-build default filter with approvalStatus: ['0']
      const defaultFilters = buildInitialFilters(null, ['0']);
      capturedCrApprovalFiltersRef.current = defaultFilters;
      setCrApprovalExternalFilters(defaultFilters);
      crApprovalPaginationRef.current = { ...INITIAL_PAGINATION };

      // Remove stale projectId from URL search params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('projectId');
      setSearchParams(newParams, { replace: true });

      triggerPaginationRender();
      return;
    }

    if (action === 'apply') {
      const normalizedFilters = normalizeCapturedFilters(filters);
      capturedCrApprovalFiltersRef.current = normalizedFilters;
      setCrApprovalExternalFilters(normalizedFilters);
      crApprovalPaginationRef.current = { ...crApprovalPaginationRef.current, page: 1 };

      // Sync URL parameter with newly selected project filter if present
      const projectFilter = normalizedFilters.find((f) => f.field === 'projectName');
      const rawVal = projectFilter?.selectedValue;
      const selectedProjectId = (rawVal !== null && typeof rawVal === 'object')
        ? rawVal?.value
        : (rawVal ?? projectFilter?.selectedValues?.[0]);

      const newParams = new URLSearchParams(searchParams);
      if (selectedProjectId) {
        newParams.set('projectId', String(selectedProjectId));
      } else {
        newParams.delete('projectId');
      }
      setSearchParams(newParams, { replace: true });

      triggerPaginationRender();
    }
  }, [searchParams, setSearchParams, normalizeCapturedFilters, triggerPaginationRender]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    let observer = null;
    const scheduleEnhancements = () => {
      if (domEnhancementRafRef.current) return;
      domEnhancementRafRef.current = window.requestAnimationFrame(() => {
        domEnhancementRafRef.current = null;
        if (observer) observer.disconnect();

        applyToolbarButtonClasses(container);
        applyTotalRowClass(container);

        if (observer) observer.observe(container, { childList: true, subtree: true });
      });
    };

    scheduleEnhancements();
    observer = new MutationObserver(() => scheduleEnhancements());
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      if (domEnhancementRafRef.current) {
        window.cancelAnimationFrame(domEnhancementRafRef.current);
        domEnhancementRafRef.current = null;
      }
      if (observer) observer.disconnect();
    };
  }, []);

  const handleCellClick = useCallback((params) => {
    const { fieldPath, action, rowData } = params || {};

    if (action === 'edit') {
      setSelectedRow(rowData);
      setEditDialogVisible(true);
    }

    if (fieldPath !== 'projectName') return;

    const selectedProjectId = rowData?.projectId || rowData?.projectDetail?.projectId;
    if (!selectedProjectId) return;

    window.open(
      `/delivery/project-details?projectId=${selectedProjectId}`,
      '_blank',
      'noopener,noreferrer',
    );
  }, []);

  const handleCrApprovalCellClick = useCallback(async (params) => {
    const { fieldPath, action, rowData } = params || {};

    if (fieldPath === 'crNumber' || action === 'view') {
      await fetchCrViewData(rowData);
      setCrDetailsDialogVisible(true);
      return;
    }

    if (fieldPath === 'projectName') {
      const selectedProjectId = rowData?.projectId || rowData?.projectDetail?.projectId;
      if (selectedProjectId && selectedProjectId !== '-') {
        window.open(
          `/delivery/project-details?projectId=${selectedProjectId}`,
          '_blank',
          'noopener,noreferrer',
        );
      }
      return;
    }

    const statusVal = rowData?.approvalStatus || rowData?.statusDetail || rowData?.status;
    const isPending
    = rowData?.is_approved === 0
    || statusVal?.toString().trim()
      .toLowerCase() === 'open'
    || statusVal?.toString().trim()
      .toLowerCase() === 'pending';

    if (fieldPath === 'approvalStatus' || fieldPath === 'statusDetail' || action === 'edit') {
      if (!isPending) {
        toast.info('Only pending requests can be modified.');
        return;
      }
      setSelectedRow(rowData);
      setCrStatusUpdateDialogVisible(true);
    }
  }, [fetchCrViewData]);

  const lastUrlProjectIdRef = useRef(searchParams.get('projectId'));

  useEffect(() => {
    const currentUrlProjectId = searchParams.get('projectId');
    const currentUrlProjectName = searchParams.get('projectName');

    // Only trigger filter update if projectId in searchParams actually changed
    if (lastUrlProjectIdRef.current === currentUrlProjectId) return;
    lastUrlProjectIdRef.current = currentUrlProjectId;

    const filters = buildInitialFilters(currentUrlProjectId, ['0'], currentUrlProjectName);
    setCrApprovalExternalFilters(filters);
    capturedCrApprovalFiltersRef.current = filters;
  }, [searchParams]);

  // Opens CR Approval History in a new tab with current project ID forwarded
  const openCrApprovalHistoryInNewTab = useCallback(() => {
    const projectFilter = capturedMainFiltersRef.current.find((f) => f.field === 'projectName');
    const rawSelectedValue = projectFilter?.selectedValue;
    const selectedProjectId = (rawSelectedValue !== null && typeof rawSelectedValue === 'object')
      ? rawSelectedValue?.value
      : (rawSelectedValue ?? projectFilter?.selectedValues?.[0]);

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('view', 'approval-history');

    if (selectedProjectId) {
      currentUrl.searchParams.set('projectId', selectedProjectId);
    }

    window.open(currentUrl.toString(), '_blank');
  }, []);

  const handleAddIconClick = useCallback(() => {
    if (!isSingleProject) {
      toast.info('Select a single project to add a new request.');
      return;
    }
    if (!canCreateCRFinal) {
      toast.error('You do not have permission to create a request.');
      return;
    }
    setSelectedRow(null);
    setNewDialogVisible(true);
  }, [isSingleProject, canCreateCRFinal]);

  const moreMenuItems = useMemo(() => [
    {
      label: 'CR Approval History',
      icon: 'pi pi-history',
      command: openCrApprovalHistoryInNewTab,
    },
  ], [openCrApprovalHistoryInNewTab]);

  const additionalElements = useMemo(() => [
    <React.Fragment key="cr-more-menu">
      <Menu
        ref={moreMenuRef}
        model={moreMenuItems}
        popup
        appendTo={document.body}
        className="change-request-more-menu"
      />
      <Button
        icon="pi pi-ellipsis-v"
        className="p-button-text change-request-more-btn"
        onClick={(e) => moreMenuRef.current?.toggle(e)}
        tooltip="More options"
        tooltipOptions={{ position: 'top', className: 'change-request-more-tooltip' }}
      />
    </React.Fragment>,
  ], [moreMenuItems]);

  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL,
        headers: { token: userProfile?.access_token || '' },
        isInitialLoad: true,
        userInfo: {
          id: userProfile?.id || '',
          emailId: userProfile?.email || '',
          permissions: userProfile?.permissions || [],
          roleDetails: userProfile?.roleDetails || [],
          employeeId: userProfile?.employeeId || '',
        },
        handlers: { handleAddIconClick },
        handler: {
          handleFilteredApiResponse,
          onCellClickHandler: handleCellClick,
          setTableStateHandler: (getState) => {
            tableStateRef.current = getState;
          },
          onFilterUpdate: handleFilterUpdate,
        },
        additionalPayloadData: {
          isFilterApplied: didUserApplyFilters,
        },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          download: {
            endpoint: 'export',
            enabled: true,
            filename: () => getExportFileName('Change_Request'),
            sheetName: () => getExportFileNameWithDate('Change_Request'),
          },
        },
        showToolBar: true,
        showPagination: true,
        toolBarSettings: {
          showAddNew: canCreateCR && canViewCRList,
          showFilterOption: true,
          showExpandCollapse: false,
          showDownloadExcel: true,
          downloadExcelSheetName: 'Change Request',
          showColumnCustomize: false,
          showHelp: false,
          showSearch: false,
          showSaveView: false,
          showToolBarHeading: true,
          toolBarHeading: 'Change Request',
          showReset: true,
          additionalElements: canViewCRApprovalHistory ? additionalElements : [],
        },
      },
      showFilterOption: true,
      tableSettings: {
        enableTreeTable: false,
        size: 'large',
        loaderSkeleton: { rows: 5, bodyCells: 1, columns: 8 },
        pageSize: PAGE_SIZE,
        paginationConfig: {
          ...mainPaginationRef.current,
          onPaginationChange: handlePaginationChange,
          rowsPerPageOptions: [10, 15, 25, 50],
        },
        showExpandCollapse: false,
        tableScrollHeight: '400px',
        maxFrozenColumns: { desktop: 4, tablet: 2, mobile: 1 },
      },
    }),
    [
      userProfile,
      canCreateCR,
      canViewCRList,
      canViewCRApprovalHistory,
      handleFilteredApiResponse,
      handlePaginationChange,
      handleFilterUpdate,
      handleCellClick,
      handleAddIconClick,
      didUserApplyFilters,
      additionalElements,
    ],
  );

  const crApprovalHistoryConfig = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL: crApprovalHistoryBaseURL,
        approvalPendingStatsBaseURL: crApprovalContext.statsURL,
        headers: { token: userProfile?.access_token || '' },
        isInitialLoad: isCrApprovalInitialLoad,
        userInfo: {
          id: userProfile?.id || '',
          emailId: userProfile?.email || '',
          permissions: userProfile?.permissions || [],
          roleDetails: userProfile?.roleDetails || [],
          employeeId: userProfile?.employeeId || '',
        },
        handler: {
          handleFilteredApiResponse: handleCrApprovalFilteredApiResponse,
          onCellClickHandler: handleCrApprovalCellClick,
          setTableStateHandler: (getState) => {
            crApprovalTableStateRef.current = getState;
          },
          onFilterUpdate: handleCrApprovalFilterUpdate,
        },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          download: {
            endpoint: 'export',
            enabled: true,
            filename: () => getExportFileName('CR_Approval_History'),
            sheetName: () => getExportFileNameWithDate('CR_Approval_History'),
          },
        },
        showToolBar: true,
        showPagination: true,
        toolBarSettings: {
          showAddNew: false,
          showFilterOption: true,
          showExpandCollapse: false,
          showDownloadExcel: false,
          downloadExcelSheetName: 'CR Approval History',
          showColumnCustomize: false,
          showHelp: false,
          showSearch: false,
          showAppliedFilters: true,
          showSaveView: false,
          showToolBarHeading: true,
          toolBarHeading: 'CR Approval History',
          showReset: true,
          additionalElements: [],
        },
      },
      showFilterOption: true,
      tableSettings: {
        enableTreeTable: false,
        size: 'large',
        loaderSkeleton: { rows: 5, bodyCells: 1, columns: 8 },
        pageSize: PAGE_SIZE,
        paginationConfig: {
          ...crApprovalPaginationRef.current,
          onPaginationChange: handleCrApprovalPaginationChange,
          rowsPerPageOptions: [10, 15, 25, 50],
        },
        showExpandCollapse: false,
        tableScrollHeight: '400px',
        maxFrozenColumns: { desktop: 4, tablet: 2, mobile: 1 },
      },
    }),
    [
      userProfile,
      handleCrApprovalFilteredApiResponse,
      handleCrApprovalPaginationChange,
      handleCrApprovalFilterUpdate,
      isCrApprovalInitialLoad,
      handleCrApprovalCellClick,
      crApprovalContext,
    ],
  );

  const refreshCurrentTableWithFilters = useCallback(() => {
    const tableState = showCRApprovalHistory
      ? crApprovalTableStateRef.current?.()
      : tableStateRef.current?.();
    if (tableState?.fetchTableData) {
      tableState.fetchTableData(
        tableState.filters,
        tableState.pagination,
        tableState.searchQuery,
      );
    } else {
      triggerPaginationRender();
    }
  }, [showCRApprovalHistory, triggerPaginationRender]);

  const handleCrStatusUpdateSubmit = async ({ status, remarks }) => {
    try {
      let crId = selectedRow?.cr_id || selectedRow?.crId || selectedRow?.id;
      if (!crId && selectedRow?.crNumber) {
        const m = String(selectedRow?.crNumber).match(/(\d+)/);
        if (m) crId = Number(m[1]);
      }

      const token = userProfile?.access_token || '';
      const rnNumber = selectedRow?.rnNumber?.replace(/RN\s*-\s*/, '');

      const payload = {
        is_approved: status,
        approved_remarks: remarks,
        id: rnNumber,
        user: {
          employee_no: userProfile?.id,
          employeeId: userProfile?.employeeId || '',
        },
      };

      const response = await approveCRRequest({ authToken: token, crId, payload });

      if (response?.status < 400) {
        toast.success(response?.data?.message || response?.message || 'Success!');
        refreshCurrentTableWithFilters();
      } else {
        toast.error(response?.data?.message || 'Something went wrong');
        refreshCurrentTableWithFilters();
      }
    } catch {
      refreshCurrentTableWithFilters();
    }

    setCrStatusFormData({ status: null, remarks: '' });
    setCrStatusUpdateDialogVisible(false);
  };

  const handleCrStatusUpdateCancel = () => {
    setCrStatusFormData({ status: null, remarks: '' });
    setCrStatusUpdateDialogVisible(false);
  };

  return (
    <>
      {!showCRApprovalHistory && canViewCRList ? (
        <div className="change-request-outer">
          <div className="change-request-table">
            <div className="change-request-table-utility" ref={tableContainerRef}>
              <TableUtility key="main-change-request-table" configuration={configuration} externalFilters={mainExternalFilters} />
            </div>
          </div>
        </div>
      ) : (
        canViewCRApprovalHistory && (
          <CRApprovalHistory
            tableKey="cr-approval-history-table"
            configuration={crApprovalHistoryConfig}
            externalFilters={crApprovalExternalFilters}
            tableContainerRef={crApprovalTableRef}
            infoMessage={infoMessage}
          />
        )
      )}

      {/* Dialog for Creating New CR */}
      {canCreateCR && canViewCRList && (
        <ChangeRequestDialog
          visible={newDialogVisible}
          onHide={() => setNewDialogVisible(false)}
          rowData={currentProject}
          isCreate={true}
          onSuccess={() => {
            setNewDialogVisible(false);
            refreshCurrentTableWithFilters();
          }}
        />
      )}

      {/* Dialog for Editing Existing CR */}
      {canEditCR && canViewCRList && (
        <ChangeRequestDialog
          visible={editDialogVisible}
          onHide={() => {
            setEditDialogVisible(false);
            setSelectedRow(null);
          }}
          rowData={selectedRow}
          isCreate={false}
          onSuccess={() => {
            setEditDialogVisible(false);
            refreshCurrentTableWithFilters();
          }}
        />
      )}

      {/* CR View Details Dialog */}
      {canViewCRDetails && (
        <CRDetailsDialog
          visible={crDetailsDialogVisible}
          onHide={() => setCrDetailsDialogVisible(false)}
          data={crDetailsData}
        />
      )}

      {/* CR Status Update Dialog */}
      {canApproveRejectCR && canViewCRApprovalHistory && (
        <CRStatusUpdateDialog
          visible={crStatusUpdateDialogVisible}
          onHide={() => {
            setCrStatusFormData({ status: null, remarks: '' });
            setCrStatusUpdateDialogVisible(false);
          }}
          onSubmit={handleCrStatusUpdateSubmit}
          onCancel={handleCrStatusUpdateCancel}
          onFormDataChange={(data) => setCrStatusFormData(data)}
        />
      )}
    </>
  );
};

export default ChangeRequest;
